import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import { Button, FormWrap, Input, TelephoneInput, Select, TextArea, toast, analyticsHelpers, hooksHelpers } from 'day8-ui';


import { ReCaptcha, loadReCaptcha } from 'react-recaptcha-google';
import { apiHost } from '../../config';
import { useSelector } from 'react-redux';

const { GA4_TRIGGERS } = analyticsHelpers;

const WaitingListFormInner = ({
  week, destination, extra, text, numOfPeople, packageFilter, toggle
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countryCode, setCountryCode] = useState('');
  const loggedUser = useSelector(state => state.user.loggedUser);
  const { formData, handleChange } = hooksHelpers.useForm({
    firstName: loggedUser?.firstName,
    lastName: loggedUser?.lastName,
    email: loggedUser?.email,
    phone: loggedUser?.phone,
    numOfPeople: numOfPeople || "Solo"
  });
  const waitingListFormRecaptcha = useRef();
  const waitingListForm = useRef();
  const formDataRef = useRef();
  formDataRef.current = formData;

  useEffect(() => {
    // Load reCAPTCHA script when component mounts
    if (typeof window.grecaptcha === 'undefined') {
      loadReCaptcha();
    }

    if (!loggedUser?.phone) {
      getCountryCode();
    }
  }, []);

  const onLoadRecaptcha = e => {
    if (e) {
      e.preventDefault();
      setLoading(true);
      toast.dismiss();
      setErrors({});
      if (!validateForm()) {
        setLoading(false);
        return false;
      }
    }
    if (waitingListFormRecaptcha && waitingListFormRecaptcha.current) {
      waitingListFormRecaptcha.current.reset();
      waitingListFormRecaptcha.current.execute();
    }
  };

  const verifyCallback = recaptchaToken => {
    // Here you will get the final recaptchaToken!!!
    if (recaptchaToken) {
      submitForm();
    } else {
      // Handle reCAPTCHA failure
      toast.error('reCAPTCHA verification failed. Please try again.', { duration: 3000 });
      setLoading(false);
    }
  };

  const getCountryCode = async () => {
    const result = await fetch(`${apiHost}/api/account/country-code`, {
      credentials: 'same-origin'
    });
    const json = await result.json();
    const countryCodeData = json.data && json.data.countrycode;
    setCountryCode(countryCodeData);
  };

  const validateForm = () => {
    const { email, firstName, lastName, phone } = formDataRef.current;
    const filter = /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    if (email === '') {
      toast.error('Please check all fields.', { duration: 3000 });
      setLoading(false);
    } else if (!filter.test(email)) {
      setErrors({ ...errors, email: 'Please check your email address is correct.' });
      setLoading(false);
    } else if (firstName.length === 0) {
      setErrors({ ...errors, firstName: 'Please enter your first name.' });
      setLoading(false);
    } else if (lastName.length === 0) {
      setErrors({ ...errors, lastName: 'Please enter your last name.' });
      setLoading(false);
    } else if (!phone) {
      setErrors({ ...errors, phone: 'Phone number is required.' });
      setLoading(false);
    } else {
      return true;
    }
    return false;
  };

  const submitForm = () => {
    const {
      email,
      firstName,
      lastName,
      extraInfo,
      phone,
      numOfPeople
    } = formDataRef.current;
    const query = {
      email,
      firstName,
      lastName,
      extraInfo,
      phone,
      week,
      destination,
      type: extra,
      numOfPeople,
      packageFilter
    };

    // https://medium.com/@dmccoy/how-to-submit-an-html-form-to-google-sheets-without-google-forms-b833952cc175
    // https://docs.google.com/spreadsheets/d/1t2ECIPjUCJACU6kDJ6sO1umYJpVYWFqdB4TM0g5U53Q

    const endpoint = 'https://script.google.com/macros/s/AKfycbz1Rds8XK6g6RBV6igXMrEYaZM0bH4j0ly5gO-HAkuEfVfq_C08Ybr-eeJh6Vu_UmHr/exec';

    fetch(`${endpoint}?${querystring.stringify(query)}`, {
      method: 'GET'
    })
      .then(response => response.json())
      .then(response => {
        if (response && response.result === 'success') {
          analyticsHelpers.joinWaitingList({ destination, week, email });
          toast.success("You're signed up!", { duration: 3000 });
          toggle();
        } else if (response.error === 'email already submitted') {
          toast.error('We\'ve already received an application from you!', { id: 'error', duration: 3000 });
        } else {
          toast.error('Something went wrong, please try again later', { id: 'error', duration: 3000 });
        }
      })
      .catch(() => {
        toast.error('Error: Something went wrong, try again', { id: 'error', duration: 3000 });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const options = Array.from({ length: 11 }, (_, i) => ({ text: (i + 1).toString(), value: i + 1 }));
  options.unshift({ text: 'Solo', value: "Solo" });
  return (
    <div>
      <p className='dui-text-gray-500 dui-font-semibold dui-text-sm'>
        {!!week && `w${week} | ` }
        {!!destination && `${destination}` }
        {!!extra && ` | ${extra}` }
        {!!packageFilter && ` | ${packageFilter}` }
      </p>
      <p className='dui-mt-4'>{text}</p>
      <FormWrap forwardRef={waitingListForm} id={GA4_TRIGGERS.WAITING_LIST_FORM_ID} className='dui-mt-4' onSubmit={onLoadRecaptcha} loading={loading}>
        <div className='dui-grid dui-grid-cols-1 sm:dui-grid-cols-2 dui-gap-4'>
          <Input
            containerClassName='dui-col-span-1 dui-w-full'
            className='dui-w-full'
            placeholder="First name"
            name="firstName"
            required
            onChange={handleChange}
            type="text"
            id="firstname-waiting-list"
            value={formData.firstName}
            label="First name*"
            errorMessage={errors?.firstName}
          />
          <Input
            className='dui-w-full'
            containerClassName='dui-col-span-1 dui-w-full'
            placeholder="Last name"
            name="lastName"
            required
            onChange={handleChange}
            type="text"
            id="lastname-waiting-list"
            value={formData.lastName}
            label="Last name*"
            errorMessage={errors?.lastName}
          />
        </div>
        <div className='dui-grid dui-grid-cols-1 dui-gap-4 dui-mt-4'>
          <Input
            containerClassName='dui-col-span-1 dui-w-full'
            className='dui-w-full'
            placeholder="Enter your email here..."
            name="email"
            required
            onChange={handleChange}
            type="email"
            id="email-waiting-list"
            value={formData.email}
            label="Email*"
            errorMessage={errors?.email}
          />
          {/* <TelephoneInput
              inputContainerClassName='dui-flex-grow'
              className='dui-w-full'
              inputClassName='dui-w-full'
              onChange={number => handleFormChange({ target: { name: 'phone', value: number } }) }
              defaultCountry={user.country}
              label="Phone number"
              errorMessage={notifications.error && notifications.error.phone?.notificationMessage}
              value={user.phone}
              required
            /> */}
          <TelephoneInput
            inputContainerClassName='dui-flex-grow'
            inputClassName="dui-w-full"
            onChange={number => handleChange({ target: { name: 'phone', value: number } })}
            value={formData.phone}
            defaultCountry={countryCode}
            label="Phone number*"
            // required
            errorMessage={errors?.phone}
          />
          <Select
            className='dui-w-full'
            containerClassName='dui-col-span-1 dui-w-full'
            placeholder="Pick an option..."
            name="numOfPeople"
            options={options}
            required
            onChange={handleChange}
            id="num-of-people-waiting-list"
            value={formData.numOfPeople}
            label="How many people will join you?"
          />
          <TextArea
            className='dui-w-full'
            onChange={handleChange}
            name="extraInfo"
            value={formData.extraInfo}
            label="Anything else we should know?"
            id="extra-info-waiting-list"
            placeholder="Enter additional information here..."
          />
        </div>
        <div>
          <div className="dui-flex dui-flex-col md:dui-flex-row dui-justify-end dui-gap-4 dui-mt-8">
            <Button className="dui-w-full" buttonType="SECONDARY" onClick={toggle}>Close</Button>
            <Button className="dui-w-full" buttonType="PRIMARY" type='submit'>{loading ? 'Submitting...' : 'Submit'}</Button>
          </div>
        </div>
        <small className="dui-text-gray-400 dui-mt-4">
          This site is protected by reCAPTCHA and the Google
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            {' '}
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>{' '}
          apply.
        </small>
        {window.grecaptcha ? (
          <ReCaptcha
            ref={waitingListFormRecaptcha}
            size="invisible"
            render="explicit"
            sitekey="6Lc9GsIUAAAAABpO3JLwFv9dmkC1lOQDNRzwGZ-0"
            onloadCallback={onLoadRecaptcha}
            verifyCallback={verifyCallback}
          />
        ) : null}
      </FormWrap>
    </div>
  );
};

const WaitingListForm = props => {
  useEffect(() => loadReCaptcha(), []);
  return (
    <WaitingListFormInner {...props} />
  );
};

WaitingListFormInner.propTypes = {
  week: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  toggle: PropTypes.func,
  extra: PropTypes.string,
  text: PropTypes.string,
  destination: PropTypes.string,
  numOfPeople: PropTypes.string,
  packageFilter: PropTypes.string
};

export default WaitingListForm;
